import UiIcon from '@/components/ui/UiIcon';
import Link from 'next/link';
import type { ChangeEventHandler, HTMLAttributes } from 'react';
import { AvatarFx } from '@/components/chat/AvatarFx';
import type { MyProfileForm } from '../../_lib/messengerSettingsConfig';

type ProfileIdentity = {
  display_name?: string | null;
  username?: string | null;
  nickname?: string | null;
  avatar_url?: string | null;
};

type MessengerProfileSettingsSectionProps = {
  myProfile: ProfileIdentity | null;
  currentUser: ProfileIdentity | null;
  avatarFxColor: string;
  avatarFxThickness: number;
  avatarFxGlow: number;
  avatarFxOfflineColor: string;
  avatarBusy: boolean;
  profileBusy: boolean;
  canDeleteAvatar: boolean;
  nicknameDraft: string;
  canSaveNickname: boolean;
  profileForm: MyProfileForm;
  onAvatarSelect: ChangeEventHandler<HTMLInputElement>;
  onDeleteAvatar: () => void;
  onNicknameChange: (value: string) => void;
  onSaveNickname: () => void;
  onProfileFieldChange: (field: keyof MyProfileForm, value: string) => void;
  onSaveProfile: () => void;
  onResetProfile: () => void;
};

const PROFILE_FIELDS: Array<{
  key: Exclude<keyof MyProfileForm, 'bio'>;
  label: string;
  placeholder: string;
  maxLength: number;
  inputMode?: HTMLAttributes<HTMLInputElement>['inputMode'];
}> = [
  { key: 'first_name', label: 'Имя', placeholder: 'Имя', maxLength: 100 },
  { key: 'last_name', label: 'Фамилия', placeholder: 'Фамилия', maxLength: 100 },
  { key: 'middle_name', label: 'Отчество', placeholder: 'Отчество', maxLength: 100 },
  { key: 'phone', label: 'Телефон', placeholder: '+7...', maxLength: 100, inputMode: 'tel' },
  { key: 'position', label: 'Должность', placeholder: 'Должность', maxLength: 100 },
  { key: 'organization', label: 'Организация', placeholder: 'Организация', maxLength: 100 },
  { key: 'website', label: 'Сайт', placeholder: 'https://...', maxLength: 200, inputMode: 'url' },
  { key: 'work_time', label: 'Время работы', placeholder: 'Пн–Пт 09:00–18:00', maxLength: 255 },
  { key: 'github_username', label: 'GitHub', placeholder: 'username', maxLength: 100, inputMode: 'text' },
  { key: 'telegram_username', label: 'Telegram', placeholder: 'username', maxLength: 100, inputMode: 'text' },
];

export function MessengerProfileSettingsSection({
  myProfile,
  currentUser,
  avatarFxColor,
  avatarFxThickness,
  avatarFxGlow,
  avatarFxOfflineColor,
  avatarBusy,
  profileBusy,
  canDeleteAvatar,
  nicknameDraft,
  canSaveNickname,
  profileForm,
  onAvatarSelect,
  onDeleteAvatar,
  onNicknameChange,
  onSaveNickname,
  onProfileFieldChange,
  onSaveProfile,
  onResetProfile,
}: MessengerProfileSettingsSectionProps) {
  const displayName = String(myProfile?.display_name || myProfile?.username || currentUser?.display_name || 'Вы');
  const fallbackLetter = String(displayName[0] || '?').toUpperCase();
  const avatarUrl = myProfile?.avatar_url ?? currentUser?.avatar_url ?? null;
  const nickname = String(myProfile?.nickname || myProfile?.username || currentUser?.nickname || currentUser?.username || '');

  return (
    <>
      <div className="text-xs text-site-muted px-1 sm:hidden">Профиль</div>
      <div className="rounded-2xl border border-site-border bg-site-bg/40 backdrop-blur-sm overflow-hidden">
        <div className="px-4 py-4 flex items-center gap-4">
          <AvatarFx
            sizeClass="w-16 h-16"
            avatarUrl={avatarUrl as any}
            alt={displayName}
            fallback={fallbackLetter}
            isOnline={true}
            fxColor={avatarFxColor}
            fxThickness={avatarFxThickness}
            fxGlow={avatarFxGlow}
            fxOfflineColor={avatarFxOfflineColor}
          />
          <div className="min-w-0 flex-1">
            <div className="text-base font-semibold text-site-text truncate">{displayName}</div>
            <div className="text-xs text-site-muted truncate">@{nickname}</div>
            <Link
              href="/profile"
              className="mt-2 inline-flex w-full sm:w-fit items-center justify-center gap-2 px-3 py-2 rounded-full glass-btn border border-site-border text-xs font-semibold text-site-text hover:text-site-text hover:bg-site-text/5 transition-colors"
              title="Открыть профиль ЛИСАД"
            >
              <span className="text-sm"><UiIcon name="FileText" /></span>
              <span>Открыть профиль ЛИСАД</span>
            </Link>
            <div className="text-[11px] text-site-success truncate">● онлайн</div>
          </div>
          <div className="flex flex-col gap-2 items-end">
            <label className="px-3 py-2 text-xs rounded-lg border border-site-border text-site-text hover:text-site-text hover:bg-site-raised/25 transition-colors cursor-pointer">
              {avatarBusy ? 'Загрузка...' : 'Изменить фото'}
              <input type="file" accept="image/*" className="hidden" disabled={avatarBusy} onChange={onAvatarSelect} />
            </label>
            {canDeleteAvatar && (
              <button
                type="button"
                onClick={onDeleteAvatar}
                disabled={avatarBusy}
                className="px-3 py-2 text-xs rounded-lg border border-site-danger/25 text-site-danger hover:bg-site-danger/10 disabled:opacity-60"
              >
                Удалить
              </button>
            )}
          </div>
        </div>

        <div className="px-4 py-3 border-t border-site-border">
          <div className="text-sm font-semibold text-site-text">Публичный @username</div>
          <div className="text-[11px] text-site-muted mt-0.5">Ссылка на профиль: https://lisad.kbsu.ru/@username (или /username)</div>
          <div className="mt-2 flex flex-col sm:flex-row gap-2">
            <input
              value={nicknameDraft}
              onChange={(event) => onNicknameChange(event.target.value)}
              maxLength={64}
              className="flex-1 px-3 py-2 bg-site-surface/60 border border-site-border rounded-lg text-sm text-site-text placeholder-site-muted focus:outline-none focus:ring-2 focus:ring-site-accent/50 select-text"
              placeholder="@username"
              disabled={profileBusy}
              inputMode="text"
            />
            <button
              type="button"
              onClick={onSaveNickname}
              disabled={profileBusy || !canSaveNickname}
              className="px-3 py-2 text-xs font-semibold rounded-lg bg-site-accent hover:bg-site-accent disabled:bg-site-raised disabled:text-site-muted text-site-on-accent transition-colors"
            >
              Сохранить @username
            </button>
          </div>
          <div className="text-[11px] text-site-muted mt-1">Правила: 3–32 символа, латиница/цифры/_"-". Менять можно 1 раз в сутки.</div>
        </div>

        <div className="px-4 py-4 border-t border-site-border">
          <div className="grid sm:grid-cols-2 gap-3">
            {PROFILE_FIELDS.map((field) => (
              <div key={field.key}>
                <div className="text-xs text-site-secondary mb-1">{field.label}</div>
                <input
                  value={profileForm[field.key]}
                  onChange={(event) => onProfileFieldChange(field.key, event.target.value)}
                  maxLength={field.maxLength}
                  className="w-full px-3 py-2 bg-site-surface/60 border border-site-border rounded-lg text-sm text-site-text placeholder-site-muted focus:outline-none focus:ring-2 focus:ring-site-accent/50"
                  placeholder={field.placeholder}
                  disabled={profileBusy}
                  inputMode={field.inputMode}
                />
              </div>
            ))}

            <div className="sm:col-span-2">
              <div className="text-xs text-site-secondary mb-1">О себе</div>
              <textarea
                value={profileForm.bio}
                onChange={(event) => onProfileFieldChange('bio', event.target.value)}
                rows={3}
                maxLength={1000}
                className="w-full px-3 py-2 bg-site-surface/60 border border-site-border rounded-lg text-sm text-site-text placeholder-site-muted focus:outline-none focus:ring-2 focus:ring-site-accent/50 resize-none"
                placeholder="Коротко о вас"
                disabled={profileBusy}
              />
              <div className="text-[11px] text-site-muted mt-1">{profileForm.bio.length}/1000</div>
            </div>
          </div>

          <div className="mt-3 flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={onSaveProfile}
              disabled={profileBusy}
              className="px-3 py-2 text-xs font-semibold rounded-lg bg-site-accent hover:bg-site-accent disabled:bg-site-raised disabled:text-site-muted text-site-on-accent transition-colors"
            >
              Сохранить профиль
            </button>
            <button
              type="button"
              onClick={onResetProfile}
              disabled={profileBusy}
              className="px-3 py-2 text-xs font-semibold rounded-lg border border-site-border text-site-text hover:text-site-text hover:bg-site-text/5 disabled:opacity-60 transition-colors"
            >
              Сбросить
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
